import { ChangeEventHandler } from 'react';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { TRIGGERS_BASE_QUERY_KEY } from 'components/domain/automation/triggers/triggersQuery';
import useDisableTriggerMutation from 'components/domain/automation/triggers/useDisableTriggerMutation';
import useEnableTriggerMutation from 'components/domain/automation/triggers/useEnableTriggerMutation';
import { ExpectedQueryData } from 'components/table/hooks/useServerFilteredTable';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { Trigger } from 'models/automation/Triggers';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

/**
 * This will optimistically update the toggle state by changing the query client
 * cache.
 */
const useStatusCellToggle = ({ triggerUid }: { triggerUid: string }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyError } = useNotify();
  const baseQueryKey = [TRIGGERS_BASE_QUERY_KEY, 'list'];

  const onMutateFactory =
    ({ isActive, uid }: { isActive: boolean; uid: string }) =>
    async () => {
      await queryClient.cancelQueries(baseQueryKey);

      // Snapshot the previous value
      const previousQueries =
        queryClient.getQueriesData<ExpectedQueryData<Trigger>>(baseQueryKey);

      queryClient.setQueriesData<ExpectedQueryData<Trigger>>(
        { queryKey: baseQueryKey },
        (previousData) => {
          if (!previousData) {
            return previousData;
          }

          return {
            ...previousData,
            data: previousData.data?.map((trigger) =>
              uid === trigger.uid ? { ...trigger, isActive } : trigger,
            ),
          };
        },
      );

      return { previousQueries };
    };

  const onError = (
    error: BaseErrorResponse,
    _: unknown,
    context?: {
      previousQueries: [QueryKey, ExpectedQueryData<Trigger> | undefined][];
    },
  ) => {
    context?.previousQueries.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data);
    });

    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.triggers.apiErrorCodes.',
        t,
      }),
    );
  };

  const onSettled = () => {
    queryClient.invalidateQueries(baseQueryKey);
  };

  const { mutate: enableTrigger } = useEnableTriggerMutation({
    onMutate: onMutateFactory({ uid: triggerUid, isActive: true }),
    onError,
    onSettled,
  });
  const { mutate: disableTrigger } = useDisableTriggerMutation({
    onMutate: onMutateFactory({ uid: triggerUid, isActive: false }),
    onError,
    onSettled,
  });

  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const isInputChecked = event.target.checked;

    if (isInputChecked) {
      enableTrigger({ triggerUid });
    } else {
      disableTrigger({ triggerUid });
    }
  };

  return { onChange };
};

export default useStatusCellToggle;
